import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/throttleTime';
import request from 'superagent';
import { updateScroll } from '../../components/MessageHistory';

import {
  SEND_GEOLOCATION,
  SEND_MESSAGE,
  RECEIVE_MESSAGE,
  SEND_USER_DATA,
  SCROLLED_TO_REQUEST_PREVIOUS_ACTIVITIES,
  LOAD_PREVIOUS_ACTIVITIES,
  loadPreviousActivities,
  loadPreviousActivitiesSuccess,
  loadPreviousActivitiesFailed,
  receiveUnreadMessage,
  readUnreadMessages,
  RECORD_BUTTON_CLICKS,
  SEND_USER_ACTION,
  SEND_USER_TYPING
} from './actions';
import { CONNECTION_CHANGE } from '../connection/actions';
import { UPDATE_DOCUMENT_VISIBILITY, SET_CHAT_PANEL_OPEN } from '../ui/actions';

/**
 * If the tab/window or chat panel are not in focus, consider a message 'unread'.
 * This message is dispatched to the parent window, and then the launcher (if present)
 * via the APIs.
 */
const recieveMessageEpic = (action$, store) =>
  action$
    .ofType(RECEIVE_MESSAGE)
    .flatMap(({ activity }) => {
      const state = store.getState();
      const { documentVisible, chatPanelVisible } = state.ui;
      const { unreadActivities } = state.history;

      if (!documentVisible || !chatPanelVisible) {
        window.parent.postMessage(
          {
            source: 'ambitChatPanel',
            type: 'unreadMessages',
            count: unreadActivities.length + 1
          },
          '*'
        );

        return Observable.of(receiveUnreadMessage(activity.id));
      }

      return Observable.empty();
    })
    .catch(() => Observable.empty());

/**
 * If both the tab/window and chat panel are in focus, consider messages 'read'.
 */
const readUnreadMessagesEpic = (action$, store) =>
  action$
    .ofType(UPDATE_DOCUMENT_VISIBILITY, SET_CHAT_PANEL_OPEN)
    .flatMap(() => {
      const state = store.getState();
      const { documentVisible, chatPanelVisible } = state.ui;

      if (documentVisible && chatPanelVisible) {
        window.parent.postMessage(
          {
            source: 'ambitChatPanel',
            type: 'unreadMessages',
            count: 0
          },
          '*'
        );

        return Observable.of(readUnreadMessages());
      }

      return Observable.empty();
    })
    .catch(() => Observable.empty());

const sendMessageEpic = (action$, store) =>
  action$.ofType(SEND_MESSAGE).map(() => {
    const state = store.getState();
    const clientActivityId =
      state.history.clientActivityBase + (state.history.clientActivityCounter - 1);
    return { type: 'Send_Message_Try', clientActivityId };
  });

const sendButtonClicksEvent = (action$, store) =>
  action$.ofType(RECORD_BUTTON_CLICKS).flatMap(action => {
    const state = store.getState();
    action.activity.from = state.connection.botConnection.user;

    state.connection.botConnection.postActivity(action.activity).subscribe();
    return Observable.empty();
  });

const trySendMessageEpic = (action$, store) =>
  action$.ofType('Send_Message_Try').flatMap(action => {
    const state = store.getState();
    const clientActivityId = action.clientActivityId;
    const activity = state.history.activities.find(
      activity => activity.channelData && activity.channelData.clientActivityId === clientActivityId
    );

    if (!activity) {
      return Observable.empty();
    }

    activity.from = state.connection.botConnection.user;

    if (state.history.clientActivityCounter === 1) {
      const capabilities = {
        type: 'ClientCapabilities',
        requiresBotState: true,
        supportsTts: true,
        supportsListening: true
        // Todo: consider implementing acknowledgesTts: true
      };

      activity.entities =
        activity.entities == null ? [capabilities] : [...activity.entities, capabilities];
    }

    return state.connection.botConnection
      .postActivity(activity)
      .map(id => ({ type: 'Send_Message_Succeed', clientActivityId, id }))
      .catch(() => Observable.of({ type: 'Send_Message_Fail', clientActivityId }));
  });

const updateScrollEpic = action$ =>
  action$
    .ofType('Send_Message_Succeed')
    .do(() => updateScroll(true))
    .ignoreElements();

const retrySendMessage = action$ =>
  action$
    .ofType('Send_Message_Retry')
    .map(action => ({ type: 'Send_Message_Try', clientActivityId: action.clientActivityId }));

const sendGeolocation = (action$, store) =>
  action$.ofType(SEND_GEOLOCATION).flatMap(() => {
    const doSendGeolocation = latlng => {
      const state = store.getState();

      state.connection.botConnection
        .postActivity({
          type: 'message',
          from: state.connection.botConnection.user,
          locale: 'en-GB',
          text: `latlng=${latlng}`
        })
        .subscribe();
    };

    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(location => {
        doSendGeolocation(`${location.coords.latitude},${location.coords.longitude}`);
      });
    } else {
      request.get('https://ipinfo.io/json').end((err, res) => {
        if (err) {
          console.log('Loading IP geolocation failed', err);
        } else {
          doSendGeolocation(res.body.loc);
        }
      });
    }

    return Observable.empty();
  });

const sendUserData = (action$, store) =>
  action$.ofType(SEND_USER_DATA).flatMap(action => {
    const state = store.getState();
    action.activity.from = state.connection.botConnection.user;
    state.connection.botConnection.postActivity(action.activity).subscribe();

    return Observable.empty();
  });

const sendUserAction = (action$, store) =>
  action$.ofType(SEND_USER_ACTION).flatMap(action => {
    const state = store.getState();
    action.activity.from = state.connection.botConnection.user;
    state.connection.botConnection.postActivity(action.activity).subscribe();

    return Observable.empty();
  });

const sendUserTyping = (action$, store) =>
  action$
    .ofType(SEND_USER_TYPING)
    .throttleTime(500)
    .mergeMap(action => {
      const state = store.getState();
      action.activity.from = state.connection.botConnection.user;
      state.connection.botConnection.postActivity(action.activity).subscribe();

      return Observable.empty();
    });

const triggerInitialLoadPreviousActivitiesEpic = (action$, { config, getState }) =>
  action$
    .ofType(CONNECTION_CHANGE)
    .take(1)
    .filter(action => action.connectionStatus === 2)
    .filter(() => config.botBaseUrl && config.rememberConversation)
    .map(() =>
      loadPreviousActivities({
        domain: config.botBaseUrl,
        userId: getState().connection.botConnection.user.id,
        skip: 0,
        before: new Date().toISOString()
      })
    );

const LOAD_PREVIOUS_ACTIVITIES_LIMIT = 20;

const loadPreviousActivitiesEpic = action$ =>
  action$.ofType(LOAD_PREVIOUS_ACTIVITIES).mergeMap(({ domain, userId, skip, before }) => {
    const qs = `?before=${before}&skip=${skip}&limit=${LOAD_PREVIOUS_ACTIVITIES_LIMIT}`;

    const request$ = Observable.create(observer => {
      const req = request
        .post(`${domain}/v3/directline/conversations/previous-activities${qs}`)
        .send({ userId })
        .end((err, res) => {
          if (err) {
            observer.error(err);
          } else {
            observer.next(res.body);
            observer.complete();
          }
        });

      return () => req.abort();
    });

    return request$
      .map(({ activities, hasNext }) =>
        loadPreviousActivitiesSuccess({
          activities,
          paginationData: {
            before,
            hasNext,
            skip: skip + LOAD_PREVIOUS_ACTIVITIES_LIMIT
          }
        })
      )
      .catch(() => Observable.of(loadPreviousActivitiesFailed()));
  });

const triggerLoadPreviousActivitiesFromInfiniteScroll = (action$, { getState, config }) =>
  action$.ofType(SCROLLED_TO_REQUEST_PREVIOUS_ACTIVITIES).map(() => {
    const state = getState();
    const { skip, before } = state.history.paginationData;

    return loadPreviousActivities({
      domain: config.botBaseUrl,
      userId: state.connection.botConnection.user.id,
      skip,
      before
    });
  });

export const epics = [
  recieveMessageEpic,
  readUnreadMessagesEpic,
  sendMessageEpic,
  trySendMessageEpic,
  retrySendMessage,
  updateScrollEpic,
  sendGeolocation,
  sendUserData,
  sendUserAction,
  sendUserTyping,
  triggerInitialLoadPreviousActivitiesEpic,
  loadPreviousActivitiesEpic,
  triggerLoadPreviousActivitiesFromInfiniteScroll,
  sendButtonClicksEvent
];



// WEBPACK FOOTER //
// ./src/reducers/history/epics.js